import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { APP_CONFIG } from '../config/constants';

const SetupSection = styled.section`
  background: transparent;
  min-height: calc(100vh - 80px);
  padding: 4rem 0;
`;

const SetupTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: -0.02em;
  line-height: 1.1;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2.25rem;
  }
`;

const SetupSubtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0 auto 4rem;
  max-width: 700px;
  line-height: 1.6;
  font-weight: 400;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 3rem;
  }
`;

const StepsContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  
  @media (max-width: 768px) {
    gap: 2rem;
  }
`;

const StepSection = styled.div`
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const StepNumber = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, #7359B3 0%, #593399 100%);
  color: white;
  border-radius: 50%;
  font-weight: 700;
  font-size: 1.2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  ${StepSection}:hover & {
    transform: scale(1.05);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
  }
`;

const StepTitle = styled.h2`
  font-size: 1.6rem;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
  font-weight: 400;
`;

const StepList = styled.ul`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
  margin: 1.25rem 0;
  padding-left: 1.5rem;
  font-size: 1.05rem;
`;

const StepListItem = styled.li`
  margin-bottom: 0.75rem;
  font-weight: 400;
  position: relative;
  
  &::marker {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
  }
`;

const ImportantNote = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.75rem;
  margin: 1.5rem 0;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
`;

const ImportantTitle = styled.h4`
  color: rgba(255, 255, 255, 0.95);
  font-weight: 700;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
`;

const ImportantText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.7;
  font-size: 1rem;
`;

const TipBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.75rem;
  margin: 1.5rem 0;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
`;

const TipTitle = styled.h4`
  color: rgba(255, 255, 255, 0.95);
  font-weight: 700;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
`;

const TipText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.7;
  font-size: 1rem;
`;

const VerificationSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  max-width: 900px;
  margin: 4rem auto 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2rem;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
  
  &:hover {
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin-top: 3rem;
    
    &::before {
      width: 200px;
      top: -1.5rem;
    }
  }
`;

const VerificationTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const VerificationList = styled.ul`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
  margin: 0;
  padding-left: 1.5rem;
  font-size: 1rem;
`;

const VerificationItem = styled.li`
  margin-bottom: 0.75rem;
  font-weight: 400;
  
  strong {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const TroubleshootingSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  max-width: 900px;
  margin: 4rem auto 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -2rem;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
  
  &:hover {
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin-top: 3rem;
    
    &::before {
      width: 200px;
      top: -1.5rem;
    }
  }
`;

const TroubleshootingTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TroubleshootingList = styled.ul`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
  margin: 0;
  padding-left: 1.5rem;
  font-size: 1rem;
`;

const TroubleshootingItem = styled.li`
  margin-bottom: 0.75rem;
  font-weight: 400;
  
  strong {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const PrivacyNote = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  max-width: 900px;
  margin: 2.5rem auto 0;
  
  &:hover {
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin-top: 2rem;
  }
`;

const VideoButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 0 auto 3rem;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.95rem;
    margin-bottom: 2.5rem;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(8px);
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1.5rem;
  max-width: 550px;
  width: 85%;
  height: 95vh;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  position: relative;
  animation: modalSlideIn 0.3s ease-out;

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    max-width: 90%;
    width: 90%;
    height: 95vh;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 1.5rem;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.95);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const VideoTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  text-align: center;
  letter-spacing: -0.01em;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  border-radius: 12px;
`;

const VideoCaption = styled.p`
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin-top: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin-top: 0.5rem;
  }
`;

const SetupAutomaticCheckins = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isVideoModalOpen) {
        setIsVideoModalOpen(false);
      }
    };

    if (isVideoModalOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVideoModalOpen]);

  return (
    <SetupSection>
      <div className="container">
        <SetupTitle>Setting Up Automatic Check-ins</SetupTitle>
        <SetupSubtitle>
          Learn how to configure {APP_CONFIG.name} to automatically register check-ins using iOS Automations
        </SetupSubtitle>

        <VideoButton onClick={() => setIsVideoModalOpen(true)}>
          ▶ Watch Setup Demo Video
        </VideoButton>

        {isVideoModalOpen && (
          <ModalOverlay onClick={() => setIsVideoModalOpen(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <CloseButton onClick={() => setIsVideoModalOpen(false)}>×</CloseButton>
              <VideoTitle>Watch the Setup Demo</VideoTitle>
              <VideoContainer>
                <Video
                  src="/setup-demo.mov"
                  controls
                  playsInline
                  preload="metadata"
                >
                  Your browser does not support the video tag.
                </Video>
              </VideoContainer>
              <VideoCaption>
                Follow along with this screen recording to see the complete setup process on an iPhone
              </VideoCaption>
            </ModalContent>
          </ModalOverlay>
        )}

        <StepsContainer>
          <StepSection>
            <StepNumber>1</StepNumber>
            <StepTitle>Open iOS Shortcuts App</StepTitle>
            <StepDescription>
              On your iPhone, open the Shortcuts app (it comes pre-installed on iOS 13+). 
              If you don't see it, you can download it from the App Store.
            </StepDescription>
          </StepSection>

          <StepSection>
            <StepNumber>2</StepNumber>
            <StepTitle>Create a New Automation</StepTitle>
            <StepDescription>
              Tap the "Automation" tab at the bottom. If you have no automations, then tap the "New Automation" button, else tap the "+" button in the top right corner to create a new automation.
            </StepDescription>
          </StepSection>

          <StepSection>
            <StepNumber>3</StepNumber>
            <StepTitle>Choose Your Trigger</StepTitle>
            <StepDescription>
              We recommend using an app open trigger for the most simple automatic check-ins. Follow these steps:
            </StepDescription>
            <StepList>
              <StepListItem>Tap "App" from the trigger options</StepListItem>
              <StepListItem>Tap "Choose" to open the app selection list</StepListItem>
              <StepListItem>Select one or more apps you use daily (like Mail, Safari, Messages, or your most-used social media app) and tap "Done" in the top right corner</StepListItem>
              <StepListItem>Choose "Run Immediately"</StepListItem>
              <StepListItem>Tap "Next" in the top right corner</StepListItem>
            </StepList>
            
            <TipBox>
              <TipTitle>💡 Pro Tip</TipTitle>
              <TipText>
                You can select multiple apps - the automation will trigger when you open any of them. 
                Choose apps you naturally use every day for the most consistent check-ins.
              </TipText>
            </TipBox>
          </StepSection>

          <StepSection>
            <StepNumber>4</StepNumber>
            <StepTitle>Add the {APP_CONFIG.name} Check-in Action</StepTitle>
            <StepDescription>
              In the search bar, type "{APP_CONFIG.name}" and select the "Register Check-in" action. 
              This will add the action to your automation.
            </StepDescription>
            
            <ImportantNote>
              <ImportantTitle>⚠️ Important</ImportantTitle>
              <ImportantText>
                Make sure you''ve downloaded the {APP_CONFIG.name} app before creating the automation or else this action will not show up. 
                You need to be logged in to the app for the action to properly register a check-in.
              </ImportantText>
            </ImportantNote>
          </StepSection>

          <StepSection>
            <StepNumber>5</StepNumber>
            <StepTitle>You're All Set!</StepTitle>
            <StepDescription>
              Your automatic check-in automation is now configured! Every time you open one of your selected apps, {APP_CONFIG.name} will automatically register a check-in, giving your loved ones peace of mind 
              that you're safe and well.
            </StepDescription>
          </StepSection>
        </StepsContainer>
        
        <VerificationSection>
          <VerificationTitle>✅ Verify Your Automation is Working</VerificationTitle>
          <VerificationList>
            <VerificationItem>
              <strong>Method 1 - Check the Home Page:</strong> Open one of the apps you selected in your automation. 
              Then go to the {APP_CONFIG.name} app and check the home page. You should see the check-in status for the current day 
              update to "check-in complete" after the automation triggers.
            </VerificationItem>
            <VerificationItem>
              <strong>Method 2 - Test Your Automation:</strong> Open one of the apps you selected in your automation. 
              Then in the {APP_CONFIG.name} app, go to the Settings tab → Verify Automation to find the "Test Your Automation" screen that shows all check-ins that would be triggered by your automations.
            </VerificationItem>
          </VerificationList>
        </VerificationSection>
        
        <TroubleshootingSection>
          <TroubleshootingTitle>🔧 Troubleshooting</TroubleshootingTitle>
          <TroubleshootingList>
            <TroubleshootingItem>
              <strong>Check-ins not registering:</strong> Make sure you're signed into {APP_CONFIG.name} and have an active internet connection.
            </TroubleshootingItem>
          </TroubleshootingList>
        </TroubleshootingSection>

        <PrivacyNote>
          <ImportantTitle>🔒 Privacy Note</ImportantTitle>
          <ImportantText>
            {APP_CONFIG.name} only registers that a check-in occurred - we don't know what specific 
            activity triggered it. Your privacy is protected while still providing peace of mind to your loved ones.
          </ImportantText>
        </PrivacyNote>
      </div>
    </SetupSection>
  );
};

export default SetupAutomaticCheckins;
